/**
 * Terrain horizon masks, ported from loader-terrain.js. Elevations are sampled
 * along 36 azimuths at 1, 3, 8, 20 and 50 km; per azimuth the largest
 * elevation angle after the Earth-curvature drop d^2 / 2Re is the minimum
 * elevation a satellite must clear. The elevation source and the cache are
 * injected; the cache entry keeps the original {t, mask} layout and key so
 * masks cached by the previous app remain valid.
 */
import type { TerrainMask } from './types';

export const TERRAIN_CACHE_PREFIX = 'ert_terrain_mask_';
export const TERRAIN_CACHE_TTL_MS = 30 * 86400 * 1000;
export const DEFAULT_AZIMUTHS = 36;
export const DEFAULT_DISTANCES_KM = [1, 3, 8, 20, 50];

const EARTH_RADIUS_KM = 6371;

export interface SamplePoint {
  lat: number;
  lon: number;
  /** Bearing in degrees; -1 for the origin. */
  az: number;
  distKm: number;
}

export interface ElevationSample {
  az: number;
  distKm: number;
  elevationM: number | null;
}

export interface MaskCacheEntry {
  /** Time the mask was stored (ms since epoch). */
  t: number;
  mask: TerrainMask;
}

export interface MaskCache {
  get(key: string): MaskCacheEntry | null;
  set(key: string, entry: MaskCacheEntry): void;
}

export interface TerrainMaskDeps {
  /** Elevation in metres for each point, in order; null where the source has no value. */
  fetchElevations: (points: { lat: number; lon: number }[]) => Promise<(number | null)[]>;
  cache?: MaskCache;
  nowMs?: () => number;
  azimuths?: number;
  distancesKm?: number[];
}

/** Cache key: prefix plus lat and lon rounded to 0.001 degrees. */
export function terrainCacheKey(lat: number, lon: number): string {
  return TERRAIN_CACHE_PREFIX + lat.toFixed(3) + '_' + lon.toFixed(3);
}

/** Great-circle destination on a 6371 km sphere; longitude normalised to [-180, 180). */
export function destinationPoint(
  latDeg: number,
  lonDeg: number,
  bearingDeg: number,
  distanceKm: number,
): { lat: number; lon: number } {
  const lat1 = (latDeg * Math.PI) / 180;
  const lon1 = (lonDeg * Math.PI) / 180;
  const brg = (bearingDeg * Math.PI) / 180;
  const d = distanceKm / EARTH_RADIUS_KM;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brg));
  const lon2 =
    lon1 +
    Math.atan2(Math.sin(brg) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return { lat: (lat2 * 180) / Math.PI, lon: (((lon2 * 180) / Math.PI + 540) % 360) - 180 };
}

/** The origin (az -1) followed by azimuths x distances radial points, azimuth-major. */
export function buildSamplePoints(
  lat: number,
  lon: number,
  azimuths: number = DEFAULT_AZIMUTHS,
  distancesKm: number[] = DEFAULT_DISTANCES_KM,
): SamplePoint[] {
  const points: SamplePoint[] = [{ lat, lon, az: -1, distKm: 0 }];
  const azStep = 360 / azimuths;
  for (let ai = 0; ai < azimuths; ai++) {
    const az = ai * azStep;
    for (const distKm of distancesKm) {
      const p = destinationPoint(lat, lon, az, distKm);
      points.push({ lat: p.lat, lon: p.lon, az, distKm });
    }
  }
  return points;
}

/** Query-string form of the points for the elevation proxy: lat,lon|lat,lon (5 decimals). */
export function formatPointsParam(points: { lat: number; lon: number }[]): string {
  return points.map((p) => p.lat.toFixed(5) + ',' + p.lon.toFixed(5)).join('|');
}

/**
 * Mask from the radial samples (azimuth-major, as buildSamplePoints orders
 * them without the origin). Per azimuth: the largest atan((h - drop) / d)
 * over the distances, 0 when no sample rises above the antenna, rounded to
 * 0.1 degree. A null origin elevation counts as 0 m.
 */
export function maskFromElevations(originElevationM: number | null, samples: ElevationSample[]): TerrainMask {
  const antennaElev = originElevationM == null ? 0 : originElevationM;
  const byAzimuth = new Map<number, ElevationSample[]>();
  for (const sample of samples) {
    const list = byAzimuth.get(sample.az);
    if (list) list.push(sample);
    else byAzimuth.set(sample.az, [sample]);
  }

  const mask: TerrainMask = [];
  for (const [az, list] of byAzimuth) {
    let maxAngle = 0;
    for (const sample of list) {
      if (sample.elevationM == null) continue;
      const distM = sample.distKm * 1000;
      const curvatureDropM = (distM * distM) / (2 * EARTH_RADIUS_KM * 1000);
      const heightAboveAntenna = sample.elevationM - antennaElev - curvatureDropM;
      if (heightAboveAntenna <= 0) continue;
      const angle = (Math.atan(heightAboveAntenna / distM) * 180) / Math.PI;
      if (angle > maxAngle) maxAngle = angle;
    }
    mask.push({ az, minEl: Math.round(maxAngle * 10) / 10 });
  }
  return mask;
}

/**
 * Mask for one site: a cache entry younger than 30 days is returned as is,
 * otherwise the elevations are fetched (one call for every sample point) and
 * the mask is computed and cached. Throws when the source returns the wrong
 * number of values.
 */
export async function fetchTerrainMask(
  lat: number,
  lon: number,
  deps: TerrainMaskDeps,
): Promise<TerrainMask> {
  const nowMs = deps.nowMs ?? Date.now;
  const key = terrainCacheKey(lat, lon);

  if (deps.cache) {
    const entry = deps.cache.get(key);
    if (entry && entry.t && nowMs() - entry.t <= TERRAIN_CACHE_TTL_MS) return entry.mask;
  }

  const points = buildSamplePoints(lat, lon, deps.azimuths, deps.distancesKm);
  const elevations = await deps.fetchElevations(points.map((p) => ({ lat: p.lat, lon: p.lon })));
  if (elevations.length !== points.length) {
    throw new Error('Elevation source returned ' + elevations.length + ' results, expected ' + points.length);
  }

  const samples: ElevationSample[] = points
    .slice(1)
    .map((p, i) => ({ az: p.az, distKm: p.distKm, elevationM: elevations[i + 1] }));
  const mask = maskFromElevations(elevations[0], samples);

  if (deps.cache) deps.cache.set(key, { t: nowMs(), mask });
  return mask;
}
